import { z } from 'zod';
import { ActivitySubjectTypeSchema, type ActivitySubjectType } from './activities.schemas';
import type { ProspectViewingType } from '../deal/prospects.schemas';
import { MANUAL_COMMUNICATION_TYPES, COMMUNICATION_OUTCOME_TOKENS } from '../common/communication-vocabulary.schema';
import { refersTo, refersToSubject } from '../common/refers-to';

// The shared plannable activity store (IMM-430.1, ADR 0035). One table keyed by
// `kind` carrying the IMM-418 model for any subject, so the note/communication
// split never has to be duplicated per module again.
//
// `APPOINTMENT` is the Deal's „Termin" — the German label lives in packages/i18n
// keyed off this token, never as the token itself (AGENTS.md: enum values become
// persisted DB and wire values, so they are English).
export const ACTIVITY_RECORD_KINDS = ['NOTE', 'COMMUNICATION', 'APPOINTMENT'] as const;
export const ActivityRecordKindSchema = z.enum(ACTIVITY_RECORD_KINDS);
export type ActivityRecordKind = z.infer<typeof ActivityRecordKindSchema>;

// OPEN (planned) → DONE (completed) / CANCELLED. „Überfällig" is derived
// read-side from `dueAt`, never stored.
export const ACTIVITY_RECORD_STATES = ['OPEN', 'DONE', 'CANCELLED'] as const;
export const ActivityRecordStateSchema = z.enum(ACTIVITY_RECORD_STATES);
export type ActivityRecordState = z.infer<typeof ActivityRecordStateSchema>;

// The composer's „Jetzt festhalten" | „Für später planen" toggle. LOG resolves to
// DONE + `occurredAt`; PLAN to OPEN + `dueAt`.
export const ACTIVITY_RECORD_MODES = ['LOG', 'PLAN'] as const;
export const ActivityRecordModeSchema = z.enum(ACTIVITY_RECORD_MODES);
export type ActivityRecordMode = z.infer<typeof ActivityRecordModeSchema>;

// Termin-Art. The owner (seller) side's five, unchanged since IMM-430.2 — the Deal's composer, in this order.
export const OWNER_APPOINTMENT_TYPES = [
  'VALUATION',
  'ON_SITE',
  'PROPERTY_SURVEY',
  'CONTRACT_SIGNING',
  'OTHER',
] as const;
// The buyer side's eight (IMM-1372) — the Interessent's composer, in this order. OTHER is shared with the owner side.
export const BUYER_APPOINTMENT_TYPES = [
  'VIEWING_ON_SITE',
  'VIEWING_VIRTUAL',
  'CONSULTATION',
  'FINANCING_CONSULTATION',
  'NEGOTIATION',
  'NOTARY',
  'HANDOVER',
  'OTHER',
] as const;
// Stored and wire tokens. Owner order first, then the buyer-only tokens — the order is not a UI order.
export const APPOINTMENT_TYPES = [
  ...OWNER_APPOINTMENT_TYPES,
  'VIEWING_ON_SITE',
  'VIEWING_VIRTUAL',
  'CONSULTATION',
  'FINANCING_CONSULTATION',
  'NEGOTIATION',
  'NOTARY',
  'HANDOVER',
] as const;
export const AppointmentTypeSchema = z.enum(APPOINTMENT_TYPES);
export type AppointmentType = z.infer<typeof AppointmentTypeSchema>;

// The two Termin-Arten that are a Besichtigung (IMM-1372.2 reads this set; it is the one owner of the rule).
export const VIEWING_APPOINTMENT_TYPES = [
  'VIEWING_ON_SITE',
  'VIEWING_VIRTUAL',
] as const satisfies ReadonlyArray<AppointmentType>;
export type ViewingAppointmentType = (typeof VIEWING_APPOINTMENT_TYPES)[number];

/** The Besichtigung kind of a Termin-Art; null for every other Termin-Art. The one owner of the mapping. */
export function viewingTypeOf(appointmentType: AppointmentType | null): ProspectViewingType | null {
  if (appointmentType === 'VIEWING_ON_SITE') return 'ON_SITE';

  if (appointmentType === 'VIEWING_VIRTUAL') return 'VIRTUAL';

  return null;
}

// A composer Termin has no end; its Besichtigung lasts an hour (IMM-1372.2 D3). Read by the Interessent's viewings
// and the calendar's composer Besichtigungen.
export const ACTIVITY_VIEWING_DURATION_MS = 60 * 60 * 1000;

/** The Termin-Arten a subject offers: an Interessent the buyer set, every other subject the owner set. */
export function appointmentTypesFor(subjectType: ActivitySubjectType): ReadonlyArray<AppointmentType> {
  return subjectType === 'PROSPECT' ? BUYER_APPOINTMENT_TYPES : OWNER_APPOINTMENT_TYPES;
}

export const ACTIVITY_RECORD_COMMUNICATION_TYPES = MANUAL_COMMUNICATION_TYPES;
export const ActivityRecordCommunicationTypeSchema = z.enum(ACTIVITY_RECORD_COMMUNICATION_TYPES);
export type ActivityRecordCommunicationType = z.infer<typeof ActivityRecordCommunicationTypeSchema>;

// The Ergebnis vocabularies (IMM-430.3). Two enums rather than one flat union
// because they belong to different kinds and the domain REJECTS a mismatch —
// a communication outcome on a Termin is a caller error, not a no-op. Carried
// over from IMM-418 for communications; the appointment set is new here.
export const COMMUNICATION_OUTCOMES = COMMUNICATION_OUTCOME_TOKENS;
export const CommunicationOutcomeSchema = z.enum(COMMUNICATION_OUTCOMES);
export type CommunicationOutcome = z.infer<typeof CommunicationOutcomeSchema>;

export const APPOINTMENT_OUTCOMES = ['ATTENDED', 'NOT_ATTENDED', 'RESCHEDULED'] as const;
export const AppointmentOutcomeSchema = z.enum(APPOINTMENT_OUTCOMES);
export type AppointmentOutcome = z.infer<typeof AppointmentOutcomeSchema>;

export const ActivityOutcomeSchema = z.union([CommunicationOutcomeSchema, AppointmentOutcomeSchema]);
export type ActivityOutcome = z.infer<typeof ActivityOutcomeSchema>;

// The successful outcomes — the ones that plan nothing next.
export const NO_FOLLOW_UP_OUTCOMES: ReadonlyArray<ActivityOutcome> = ['REACHED', 'ATTENDED'];

export const ACTIVITY_RECORD_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const;
export const ActivityRecordDirectionSchema = z.enum(ACTIVITY_RECORD_DIRECTIONS);
export type ActivityRecordDirection = z.infer<typeof ActivityRecordDirectionSchema>;

// Same bounds as the lead-side model this store generalises, so IMM-512 can fold
// those rows on without re-validating them.
export const ACTIVITY_RECORD_CONTENT_MAX_LENGTH = 10_000;
export const ACTIVITY_RECORD_SUBJECT_MAX_LENGTH = 200;
export const ACTIVITY_RECORD_DURATION_MAX_MINUTES = 1_440;

// Extra records the activity should also surface on. The subject itself is
// always linked by the handler, so this carries only the *additional* records —
// today only the deal's contact, which keeps a deal activity visible on that
// contact's timeline.
//
// `recordType` is deliberately narrower than `ActivitySubjectType`: the ids here
// are caller-asserted and resolved against nothing, so accepting every subject
// type would let any member write `activity_links` rows keyed to arbitrary uuids.
// Widen it in IMM-512, where leads and contacts fold onto the store and links
// become load-bearing enough to justify per-type lookup ports.
export const ACTIVITY_RECORD_LINK_TYPES = ['CONTACT'] as const;
export const ActivityRecordLinkTypeSchema = z.enum(ACTIVITY_RECORD_LINK_TYPES);
export type ActivityRecordLinkType = z.infer<typeof ActivityRecordLinkTypeSchema>;

export const ActivityRecordLinkSchema = z.object({
  recordType: ActivityRecordLinkTypeSchema,
  recordId: z.uuid(),
});
export type ActivityRecordLink = z.infer<typeof ActivityRecordLinkSchema>;

// What the projection is told to link. Wider than the request's `links` because
// it also carries the SUBJECT — which the handler adds and is therefore trusted,
// where the caller-supplied entries above are not.
export const ActivityProjectionLinkSchema = z.object({
  recordType: ActivitySubjectTypeSchema,
  recordId: z.uuid(),
});
export type ActivityProjectionLink = z.infer<typeof ActivityProjectionLinkSchema>;

const RecordActivityCommonShape = {
  subjectType: ActivitySubjectTypeSchema,
  subjectId: z.uuid(),
  mode: ActivityRecordModeSchema,
  occurredAt: z.iso.datetime().optional(),
  dueAt: z.iso.datetime().optional(),
  content: z.string().min(1).max(ACTIVITY_RECORD_CONTENT_MAX_LENGTH),
  assigneeMemberId: refersTo('member', z.uuid()).optional(),
  links: z.array(ActivityRecordLinkSchema).optional(),
};

// The write body, discriminated on `kind` so each kind's own attributes are
// structurally impossible to send against the wrong one. IMM-430.1 accepts
// `mode: 'LOG'` and the NOTE / COMMUNICATION kinds only; PLAN and APPOINTMENT
// are rejected by the domain draft and unlocked by IMM-430.2.
export const RecordActivityRecordBodySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('NOTE'),
    ...RecordActivityCommonShape,
  }),
  z.object({
    kind: z.literal('COMMUNICATION'),
    ...RecordActivityCommonShape,
    type: ActivityRecordCommunicationTypeSchema,
    direction: ActivityRecordDirectionSchema,
    subject: z.string().max(ACTIVITY_RECORD_SUBJECT_MAX_LENGTH).optional(),
    durationMinutes: z.number().int().min(0).max(ACTIVITY_RECORD_DURATION_MAX_MINUTES).optional(),
    outcome: CommunicationOutcomeSchema.optional(),
  }),
  z.object({
    kind: z.literal('APPOINTMENT'),
    ...RecordActivityCommonShape,
    appointmentType: AppointmentTypeSchema,
  }),
]);
export type RecordActivityRecordBody = z.infer<typeof RecordActivityRecordBodySchema>;

export const RecordActivityRecordPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

// The flat persisted record. Kind-specific attributes are nullable rather than
// absent so one response type serves every kind — the store is one table.
export const ActivityRecordResponseSchema = z.object({
  id: z.uuid(),
  version: z.number().int().positive(),
  kind: ActivityRecordKindSchema,
  subjectType: ActivitySubjectTypeSchema,
  subjectId: refersToSubject('subjectType', z.uuid(), { sibling: 'subjectRef' }),
  state: ActivityRecordStateSchema,
  occurredAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable(),
  assigneeMemberId: refersTo('member', z.uuid()).nullable(),
  outcome: ActivityOutcomeSchema.nullable(),
  content: z.string(),
  type: ActivityRecordCommunicationTypeSchema.nullable(),
  direction: ActivityRecordDirectionSchema.nullable(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  appointmentType: AppointmentTypeSchema.nullable(),
  createdByActorId: refersTo('member', z.uuid()).nullable(),
  createdAt: z.iso.datetime(),
});
export type ActivityRecordResponse = z.infer<typeof ActivityRecordResponseSchema>;

// The MCP detail read's shape: the stored record plus the Standort it belongs to.
// Additive and separate — the HTTP leaves over ActivityRecordResponseSchema do not
// need the organisation and do not change.
export const ActivityRecordEmailSchema = z.object({
  providerThreadId: z.string(),
  mailboxMemberId: refersTo('member', z.uuid()),
  counterpartEmail: z.string().nullable(),
  counterpartName: z.string().nullable(),
  preview: z.string().nullable(),
  hasAttachments: z.boolean(),
  placement: z.enum(['AUTOMATIC', 'MANUAL']),
  conversationAvailable: z.boolean(),
});
export type ActivityRecordEmail = z.infer<typeof ActivityRecordEmailSchema>;
export const ActivityRecordDetailResponseSchema = ActivityRecordResponseSchema.extend({
  organizationId: refersTo('organization', z.uuid()).nullable(),
  email: ActivityRecordEmailSchema.nullable(),
});
export type ActivityRecordDetailResponse = z.infer<typeof ActivityRecordDetailResponseSchema>;

// The content types the store's projection writes onto the unified timeline, one
// per kind. STABLE — an entry already written keys on the string, so renaming one
// orphans every historical row. Per-kind (rather than one discriminated string)
// because `activity-timeline.tsx` branches on exact `contentType` equality.
export const ACTIVITY_RECORD_CONTENT_TYPES = {
  NOTE: 'activity.note-recorded',
  COMMUNICATION: 'activity.communication-recorded',
  APPOINTMENT: 'activity.appointment-recorded',
} as const;

// ── Transitions (IMM-430.3) ────────────────────────────────────────────────────

export const ActivityTransitionPathParamsSchema = z.object({
  organizationId: z.uuid(),
  activityId: z.uuid(),
});

export const CompleteActivityRecordBodySchema = z.object({
  occurredAt: z.iso.datetime().optional(),
  outcome: ActivityOutcomeSchema.optional(),
  note: z.string().max(ACTIVITY_RECORD_CONTENT_MAX_LENGTH).optional(),
  // Overrides the default „Fällig am" of the auto-created follow-up. Refused
  // when the outcome mandates no follow-up, so a caller who believes they are
  // scheduling one is told they are not.
  followUpDueAt: z.iso.datetime().optional(),
});
export type CompleteActivityRecordBody = z.infer<typeof CompleteActivityRecordBodySchema>;

export const DeleteActivityRecordResponseSchema = z.object({ id: z.uuid() });
export type DeleteActivityRecordResponse = z.infer<typeof DeleteActivityRecordResponseSchema>;

export const CompleteActivityRecordResponseSchema = z.object({
  id: z.uuid(),
  kind: ActivityRecordKindSchema,
  state: ActivityRecordStateSchema,
  occurredAt: z.iso.datetime(),
  outcome: ActivityOutcomeSchema.nullable(),
  content: z.string(),
  // The auto-created follow-up, or null when the outcome mandated none. Returned
  // rather than left to a refetch: the projection is asynchronous, so a refetch
  // would race it.
  followUp: z.object({ activityId: z.uuid(), dueAt: z.iso.datetime() }).nullable(),
});
export type CompleteActivityRecordResponse = z.infer<typeof CompleteActivityRecordResponseSchema>;

export const RescheduleActivityRecordBodySchema = z.object({ dueAt: z.iso.datetime() });
export type RescheduleActivityRecordBody = z.infer<typeof RescheduleActivityRecordBodySchema>;

export const CancelActivityRecordBodySchema = z.object({
  reason: z.string().max(ACTIVITY_RECORD_CONTENT_MAX_LENGTH).optional(),
});
export type CancelActivityRecordBody = z.infer<typeof CancelActivityRecordBodySchema>;

export const ReassignActivityRecordBodySchema = z.object({ assigneeMemberId: z.uuid() });
export type ReassignActivityRecordBody = z.infer<typeof ReassignActivityRecordBodySchema>;

// ── „Meine Aufgaben" (IMM-430.4) ──────────────────────────────────────────────
//
// One open, assigned activity as the tasks page renders it. Flat and
// self-describing on purpose: the page merges this with the lead `tasks` read
// client-side, so a row must carry everything needed to sort and render it
// without a second round-trip per row.
//
// `terminArt` in the slice's pinned contract is anglicised to `appointmentType`
// here, matching the store's own field and the no-German-in-code rule that
// IMM-430.1 already applied to the kind token and the column.
export const ActivityTaskItemSchema = z.object({
  activityId: z.uuid(),
  kind: ActivityRecordKindSchema,
  // Set only for an APPOINTMENT; null for every other kind.
  appointmentType: AppointmentTypeSchema.nullable(),
  // Set only for a COMMUNICATION; null for every other kind. Carried because
  // „Meine Aufgaben" labels the row by it — without it a planned Anruf renders
  // as „Notiz", which is what shipped in IMM-430.4 and an e2e journey caught.
  // The lead task item has always carried its equivalent.
  type: ActivityRecordCommunicationTypeSchema.nullable(),
  // Travels with `type`, and is null exactly when it is: the store's CHECK makes
  // the pair null-or-non-null together, so the contract states that same
  // invariant rather than inventing a second one. Carried because „Meine
  // Aufgaben" must name a row the way the Deal's own Aktivität area names it,
  // and „Anruf" without „Ausgehend" is a different task (IMM-639).
  direction: ActivityRecordDirectionSchema.nullable(),
  content: z.string(),
  dueAt: z.iso.datetime(),
  // Derived per item at read time (`dueAt < now`), never stored — the shipped
  // IMM-418 rule.
  overdue: z.boolean(),
  subjectType: ActivitySubjectTypeSchema,
  subjectId: refersToSubject('subjectType', z.uuid()),
  // The subject's display label — an Objekt address for a DEAL. Null when the
  // subject resolves to no label, so a row still renders rather than vanishing.
  subjectLabel: z.string().nullable(),
  organizationId: refersTo('organization', z.uuid()),
});
export type ActivityTaskItem = z.infer<typeof ActivityTaskItemSchema>;

// Workspace-addressed: the tasks page is a workspace surface, and the read spans
// every Standort the caller can see rather than one organisation.
export const ListMemberActivityTasksPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const ListMemberActivityTasksQuerySchema = z.object({
  // Matched deliberately to the lead read's limit by the caller, so the
  // client-side merge sorts two comparable sets rather than a full page of one
  // against a partial page of the other.
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type ListMemberActivityTasksQuery = z.infer<typeof ListMemberActivityTasksQuerySchema>;

export const ListMemberActivityTasksResponseSchema = z.object({
  items: z.array(ActivityTaskItemSchema),
  // True when the store held more rows than the limit. Surfaced because a
  // client-side sort over two capped lists is only honest if neither was
  // silently cut.
  truncated: z.boolean(),
});
export type ListMemberActivityTasksResponse = z.infer<typeof ListMemberActivityTasksResponseSchema>;
